import { Center, Stack } from "@chakra-ui/react";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { LoadingOverlay } from "~/components/LoadingOverlay/LoadingOverlay";
import { NotFound } from "~/components/NotFound/NotFound";
import {
  useGetProjectQuery,
  type GetProjectEnviroDatasetsQuery,
  type GetProjectQuery,
} from "~/gql/generated";
import { ProjectAppBar } from "./ProjectAppBar";

export type Project = Pick<
  GetProjectQuery["project"],
  | "id"
  | "name"
  | "pvsyst_import_in_progress"
  | "org_general_access"
  | "organization_id"
  | "blocks_bounding_box"
  | "project_users"
  | "sentinel_deployment"
>;
export type EnviroDataset =
  GetProjectEnviroDatasetsQuery["projectEnviroDatasets"][number]["enviro_dataset"];

// TODO: should just be its own context instead of react router context
export interface ProjectProviderContextValue {
  isLoading: boolean;
  project: Project;
  isLocked: boolean;
  setIsLocked: React.Dispatch<React.SetStateAction<boolean>>;
  projectId: string;
  refetchProject: () => Promise<any>;
}

const ProjectProviderContext =
  createContext<ProjectProviderContextValue | null>(null);

export function useOptionalProjectContext() {
  return useContext(ProjectProviderContext);
}

export function useProjectContext() {
  const ctx = useContext(ProjectProviderContext);
  if (!ctx) {
    throw new Error("useProjectContext must be used within a ProjectProvider");
  }
  return ctx;
}

export const ProjectProvider = ({ children }: { children?: ReactNode }) => {
  const { projectId } = useParams<{ projectId: string }>();

  if (!projectId) {
    return null;
  }

  const {
    data,
    isLoading,
    refetch: refetchProject,
  } = useGetProjectQuery(
    {
      id: projectId,
    },
    { refetchOnMountOrArgChange: true }
  );

  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    if (data) {
      setIsLocked(Boolean(data.project.sentinel_deployment));
    }
  }, [data]);

  if (!data) {
    if (isLoading) {
      return (
        <Stack w="100%" spacing={0} h="100vh">
          <ProjectAppBar />
          <Center h="100%">
            <LoadingOverlay isLoading />
          </Center>
        </Stack>
      );
    } else {
      return <NotFound resource={"project"} />;
    }
  }

  const context: ProjectProviderContextValue = {
    isLoading,
    project: data.project,
    projectId,
    refetchProject,
    isLocked,
    setIsLocked,
  };

  return (
    <ProjectProviderContext.Provider value={context}>
      {children}
    </ProjectProviderContext.Provider>
  );
};
